import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, ArrowLeftRight, CheckCircle2, Mail, RefreshCw, ShieldAlert, Undo2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { inventoryApi, errorText } from '../../lib/inventoryApi';
import { formatAgo, formatSince } from '../../lib/inventoryTable';
import {
  SEVERITIES, SEVERITY_LABEL, TIER_LABEL, alertRows, assignBody, assignProblem, capacityParams, liveSlotLines, needsConfirm,
  notCountedTag, planOptions, problemParams, scanAlreadyRunning, searchProblems, severityClass, shortTime, typeSummary,
} from '../../lib/inventoryOps';
import { Chip, LinkPicker } from './InventoryTable';
import { cn } from '@/lib/utils';

/*
 * Inventory Phase 3 views (r16; NecroGrowth docs/01-inventory/01.5-phase2-3.md):
 *  - ProblemsView: the Problems board (missing phones, accounts with no phone / on two profiles, a phone under several
 *    device ids, version drift, challenged / locked accounts, slots on retired phones, ip:port phones, hot / low battery,
 *    PCs offline), filters, and for the admin "Scan now" / "Preview digest".
 *  - CapacityView: free profiles per phone / PC / location and "can I take a client with N accounts?".
 *  - AssignDialog: assign / move / release an account (dry run first; a LIVE slot needs an explicit confirm).
 *  - AssignmentsLog: every operation with Undo (admin).
 * Everything is scoped by the server: a client gets its own part only, and only the admin is offered write controls.
 */

const btn = 'inline-flex h-8 items-center gap-1 rounded-md border border-border px-2.5 text-xs hover:bg-secondary disabled:opacity-50';
const btnPrimary = 'inline-flex h-8 items-center gap-1 rounded-md bg-primary px-2.5 text-xs font-medium text-primary-foreground disabled:opacity-50';
const th = 'px-2 py-1.5 text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground';
const td = 'px-2 py-1.5 align-top';

function SeverityBadge({ severity }) {
  return (
    <span className={cn('inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium', severityClass(severity))}>
      {SEVERITY_LABEL[severity] || severity}
    </span>
  );
}

function Kpi({ label, value, hint, tone }) {
  return (
    <div className="min-w-[110px] rounded-lg border border-border px-3 py-2" title={hint}>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={cn('text-xl font-semibold tabular-nums', tone)}>{value ?? '-'}</div>
    </div>
  );
}

// ================================================================================================= Problems

export function ProblemsView({ isAdmin, onChipClick }) {
  const [type, setType] = useState('');
  const [severity, setSeverity] = useState('');
  const [q, setQ] = useState('');
  const [digest, setDigest] = useState(null);
  const params = problemParams({ type, severity });
  const boardQ = useQuery({
    queryKey: ['inventory-problems', JSON.stringify(params)],
    queryFn: () => inventoryApi.problems(params).then((r) => r.data),
    retry: false,
  });
  const board = boardQ.data;
  const rows = useMemo(() => searchProblems(board?.problems, q), [board, q]);
  const summary = typeSummary(board);
  // INV-7b: the alert episodes and their support tickets (admin) - refreshed after "Scan now".
  const alertsQ = useQuery({
    queryKey: ['inventory-alerts'],
    queryFn: () => inventoryApi.alerts({ size: 100 }).then((r) => r.data),
    enabled: !!isAdmin,
    retry: false,
  });
  const alerts = useMemo(() => alertRows(alertsQ.data), [alertsQ.data]);
  const openAlerts = alerts.filter((a) => a.state !== 'resolved').length;

  const [scanning, setScanning] = useState(false);
  const scanInFlight = useRef(false);
  const scan = async () => {
    // Phase 3 review must-fix 4: one scan at a time - a second click while one runs does nothing.
    if (scanInFlight.current) return;
    scanInFlight.current = true;
    setScanning(true);
    try {
      const r = await inventoryApi.alertScan();
      toast.success(`Scan done: ${r.data.ticketsFiled} ticket(s) filed, ${r.data.open} open, ${r.data.resolved} resolved`);
      if (alertsQ.refetch) alertsQ.refetch();
    } catch (e) {
      if (scanAlreadyRunning(e)) toast.info(errorText(e));
      else toast.error(errorText(e, 'Scan refused'));
    } finally {
      scanInFlight.current = false;
      setScanning(false);
    }
  };
  const previewDigest = async () => {
    try {
      const r = await inventoryApi.digestPreview();
      setDigest(r.data);
    } catch (e) {
      toast.error(errorText(e, 'No digest preview'));
    }
  };

  if (boardQ.error) return <p className="text-sm text-danger" role="alert">{errorText(boardQ.error)}</p>;
  const th3 = board?.thresholds || {};
  return (
    <div className="space-y-3" data-testid="inv-problems">
      <div className="flex flex-wrap items-center gap-2">
        {SEVERITIES.map((s) => (
          <button key={s} type="button" onClick={() => setSeverity(severity === s ? '' : s)}
            aria-pressed={severity === s} data-testid={`inv-sev-${s}`}
            className={cn('inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium', severityClass(s),
              severity === s && 'ring-2 ring-primary/40')}>
            {SEVERITY_LABEL[s]} <span className="tabular-nums">{board?.bySeverity?.[s] ?? 0}</span>
          </button>
        ))}
        <span className="text-xs text-muted-foreground">{board ? `${board.total} problem(s)` : 'Loading…'}</span>
        {isAdmin && (
          <span className="ml-auto flex items-center gap-2">
            <span className="text-xs text-muted-foreground" data-testid="inv-alerts-state">
              {board?.alertsOn ? 'Alerts ON: high and medium problems are filed as support tickets (one per problem).' : 'Alerts are switched off.'}
            </span>
            <button type="button" className={btn} onClick={scan} disabled={!board?.alertsOn || scanning}>
              <RefreshCw className={cn('h-3.5 w-3.5', scanning && 'animate-spin')} /> {scanning ? 'Scanning…' : 'Scan now'}
            </button>
            <button type="button" className={btn} onClick={previewDigest}><Mail className="h-3.5 w-3.5" /> Preview digest</button>
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5" data-testid="inv-problem-types">
        {summary.map((t) => (
          <button key={t.type} type="button" title={t.description} onClick={() => setType(type === t.type ? '' : t.type)}
            aria-pressed={type === t.type}
            className={cn('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs', type === t.type ? 'border-primary bg-primary/10' : 'border-border')}>
            {t.label} <span className="font-semibold tabular-nums">{t.count}</span>
          </button>
        ))}
        {(type || severity) && (
          <button type="button" className="text-xs text-muted-foreground underline" onClick={() => { setType(''); setSeverity(''); }}>clear filters</button>
        )}
        <input aria-label="Search problems" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
          className="ml-auto h-8 w-48 rounded-md border border-border bg-background px-2 text-xs" />
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-xs">
          <thead className="bg-secondary/40">
            <tr>
              <th className={th}>Severity</th>
              <th className={th}>Problem</th>
              <th className={th}>Record</th>
              {isAdmin && <th className={th}>Client</th>}
              <th className={th}>Since</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.key} className="border-t border-border" data-testid="inv-problem-row">
                <td className={td}><SeverityBadge severity={p.severity} /></td>
                <td className={td}>
                  <div className="font-medium">{p.title}</div>
                  <div className="text-muted-foreground">{p.detail}</div>
                  {(p.links || []).length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {p.links.map((c) => <Chip key={`${c.table}-${c.id}`} chip={c} onClick={onChipClick} />)}
                    </div>
                  )}
                </td>
                <td className={td}>{p.record ? <Chip chip={p.record} onClick={onChipClick} /> : <span className="text-muted-foreground">{p.label}</span>}</td>
                {isAdmin && <td className={td}>{p.client ? <Chip chip={p.client} onClick={onChipClick} /> : <span className="text-muted-foreground">-</span>}</td>}
                <td className={cn(td, 'whitespace-nowrap text-muted-foreground')}>{formatSince(p.sinceMin)}</td>
              </tr>
            ))}
            {board && rows.length === 0 && (
              <tr><td colSpan={isAdmin ? 5 : 4} className="px-3 py-6 text-center text-muted-foreground" data-testid="inv-no-problems">
                <CheckCircle2 className="mr-1 inline h-4 w-4 text-success" /> No problems{type || severity || q ? ' match these filters' : ''}.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      {board && (
        <p className="text-[11px] text-muted-foreground" data-testid="inv-thresholds">
          Missing = not reported for more than {th3.missingMinutes} min while its PC reports. Hot = {th3.hotC} C or more.
          Low battery = {th3.lowBatteryPct} % or less and not on power. App drift compares with the fleet&apos;s most common
          build (Instagram {th3.igTarget || '-'}{th3.tiktokTarget ? `, TikTok ${th3.tiktokTarget}` : ''}).
        </p>
      )}

      {isAdmin && (
        <details className="rounded-lg border border-border" data-testid="inv-alerts">
          <summary className="cursor-pointer px-3 py-2 text-xs font-medium">
            Alert tickets: {openAlerts} open, {alerts.length - openAlerts} resolved
            <span className="ml-2 font-normal text-muted-foreground">(one support ticket per high / medium problem; staff work them in the support queue)</span>
          </summary>
          {alertsQ.error ? (
            <p className="px-3 pb-2 text-xs text-danger" role="alert">{errorText(alertsQ.error)}</p>
          ) : (
            <div className="overflow-x-auto border-t border-border">
              <table className="w-full text-xs">
                <thead className="bg-secondary/40">
                  <tr>
                    <th className={th}>Severity</th>
                    <th className={th}>Problem</th>
                    <th className={th}>Ticket</th>
                    <th className={th}>State</th>
                    <th className={th}>First seen</th>
                    <th className={th}>Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.map((a) => (
                    <tr key={a.id} className="border-t border-border" data-testid="inv-alert-row">
                      <td className={td}><SeverityBadge severity={a.severity} /></td>
                      <td className={td}>{a.title}{a.client ? <span className="ml-1 text-muted-foreground">({a.client.label})</span> : null}</td>
                      <td className={cn(td, 'whitespace-nowrap font-mono')}>{a.ticket || <span className="text-muted-foreground">waiting</span>}</td>
                      <td className={cn(td, 'whitespace-nowrap')}>{a.state}{a.reopened > 0 ? ` (${a.reopened}x)` : ''}</td>
                      <td className={cn(td, 'whitespace-nowrap text-muted-foreground')}>{shortTime(a.firstSeenAt)}</td>
                      <td className={cn(td, 'whitespace-nowrap text-muted-foreground')}>{shortTime(a.resolvedAt || a.lastSeenAt)}</td>
                    </tr>
                  ))}
                  {alerts.length === 0 && (
                    <tr><td colSpan={6} className="px-3 py-4 text-center text-muted-foreground">No alert tickets yet - "Scan now" files them, the server scans every 5 min.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </details>
      )}

      <Dialog open={!!digest} onOpenChange={(o) => { if (!o) setDigest(null); }}>
        <DialogContent className="max-w-2xl" data-testid="inv-digest-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="h-4 w-4" /> {digest?.subject}</DialogTitle>
            <DialogDescription>
              The daily email ({digest?.recipients} recipient(s): {(digest?.recipientsMasked || []).join(', ')}). This preview sends nothing.
            </DialogDescription>
          </DialogHeader>
          <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap rounded-md bg-secondary/40 p-3 text-xs">{digest?.body}</pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ================================================================================================= Capacity

export function CapacityView({ isAdmin, onChipClick }) {
  const [form, setForm] = useState({ platform: 'INSTAGRAM', accounts: '', clientId: null, clientLabel: '' });
  const [asked, setAsked] = useState(() => capacityParams({ platform: 'INSTAGRAM' }));
  const [picking, setPicking] = useState(false);
  const capQ = useQuery({
    queryKey: ['inventory-capacity', JSON.stringify(asked)],
    queryFn: () => inventoryApi.capacity(asked).then((r) => r.data),
    retry: false,
  });
  const c = capQ.data;
  const t = c?.totals || {};
  const a = c?.answer;
  const check = (e) => {
    e?.preventDefault?.();
    setAsked(capacityParams(form));
  };

  return (
    <div className="space-y-3" data-testid="inv-capacity">
      <form onSubmit={check} className="flex flex-wrap items-end gap-2 rounded-lg border border-border p-3">
        <label className="text-xs">
          <span className="block text-muted-foreground">Platform</span>
          <select aria-label="Platform" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}
            className="h-8 rounded-md border border-border bg-background px-2 text-xs">
            <option value="INSTAGRAM">Instagram</option>
            <option value="TIKTOK">TikTok</option>
          </select>
        </label>
        <label className="text-xs">
          <span className="block text-muted-foreground">Accounts to place</span>
          <input aria-label="Accounts to place" type="number" min="1" value={form.accounts}
            onChange={(e) => setForm({ ...form, accounts: e.target.value })} placeholder="e.g. 20"
            className="h-8 w-28 rounded-md border border-border bg-background px-2 text-xs" />
        </label>
        {isAdmin && (
          <div className="text-xs">
            <span className="block text-muted-foreground">For client (empty = a new client: the pool)</span>
            {picking ? (
              /* INV-7b: no "Clear link" here ("pool" clears the client) - it sent onPick(null) and threw on ch.id. */
              <LinkPicker refTable="clients" allowClear={false}
                onPick={(ch) => { if (ch) setForm({ ...form, clientId: ch.id, clientLabel: ch.label }); setPicking(false); }}
                onCancel={() => setPicking(false)} />
            ) : (
              <div className="flex items-center gap-1">
                <button type="button" className={btn} onClick={() => setPicking(true)}>{form.clientLabel || 'New client (pool)'}</button>
                {form.clientId && <button type="button" className="text-xs underline" onClick={() => setForm({ ...form, clientId: null, clientLabel: '' })}>pool</button>}
              </div>
            )}
          </div>
        )}
        <button type="submit" className={btnPrimary}>Check</button>
        {c?.scope && <span className="ml-auto text-xs text-muted-foreground">Counting: {c.scope}</span>}
      </form>

      {capQ.error && <p className="text-sm text-danger" role="alert">{errorText(capQ.error)}</p>}

      {a && (
        <div data-testid="inv-capacity-answer"
          className={cn('rounded-lg border p-3 text-sm', a.canTake ? 'border-success/30 bg-success/10' : 'border-warning/30 bg-warning/10')}>
          <div className="flex items-center gap-2 font-medium">
            {a.canTake ? <CheckCircle2 className="h-4 w-4 text-success" /> : <AlertTriangle className="h-4 w-4 text-warning" />}
            {a.text}
          </div>
          <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
            {(a.tiers || []).map((x) => <span key={x.tier}>{x.profiles} {x.label}</span>)}
          </div>
          {(a.plan || []).length > 0 && (
            <details className="mt-2 text-xs">
              <summary className="cursor-pointer">The plan: {a.plan.length} profile(s), the client&apos;s own phones first, fullest phone first</summary>
              <ol className="mt-1 list-decimal pl-5">
                {a.plan.map((p) => (
                  <li key={p.profileId}>user {p.androidUserId} on <Chip chip={p.phone} onClick={onChipClick} /> <span className="text-muted-foreground">({TIER_LABEL[p.tier]})</span></li>
                ))}
              </ol>
            </details>
          )}
        </div>
      )}

      {c && (
        <div className="flex flex-wrap gap-2" data-testid="inv-capacity-kpis">
          <Kpi label="Phones" value={t.phones} hint="Active and spare phones counted (repair / retired are not)" />
          <Kpi label="Profiles" value={t.profiles} hint="Live Android profiles on those phones" />
          <Kpi label="Used" value={t.used} hint="Profiles holding an account" />
          <Kpi label="Free" value={t.free} hint="Profiles holding no account, still on the phone" />
          <Kpi label="Ready now" value={t.usableNow} tone="text-success" hint="Free, app installed, active phone online" />
        </div>
      )}

      {isAdmin && (c?.byHost || []).length > 0 && (
        <CapacityGroupTable title="Per PC / box" rows={c.byHost} first={(g) => (g.host ? <Chip chip={g.host} onClick={onChipClick} /> : g.label)}
          extra={(g) => (g.online ? <span className="text-success">online</span> : <span className="text-muted-foreground">offline</span>)} extraLabel="PC" />
      )}
      {(c?.byLocation || []).length > 0 && (
        <CapacityGroupTable title="Per location" rows={c.byLocation} first={(g) => g.label} />
      )}
      {c && (
        <div className="overflow-x-auto rounded-lg border border-border">
          <div className="border-b border-border px-3 py-2 text-xs font-medium">Per phone</div>
          <table className="w-full text-xs" data-testid="inv-capacity-phones">
            <thead className="bg-secondary/40">
              <tr>
                <th className={th}>Phone</th><th className={th}>Status</th><th className={th}>Seen</th>
                {isAdmin && <th className={th}>Client</th>}{isAdmin && <th className={th}>PC</th>}
                <th className={th}>Profiles</th><th className={th}>Used</th><th className={th}>Free</th><th className={th}>Ready now</th>
                <th className={th}>Free user ids</th>
              </tr>
            </thead>
            <tbody>
              {(c.phones || []).map((p) => (
                <tr key={p.phone.id} className={cn('border-t border-border', !p.eligible && 'opacity-60')}>
                  <td className={td}><Chip chip={p.phone} onClick={onChipClick} />
                    {notCountedTag(p) && (
                      <span className="ml-1 text-muted-foreground" title={p.whyNot || ''} data-testid="inv-capacity-not-counted">({notCountedTag(p)})</span>
                    )}
                  </td>
                  <td className={td}>{p.status || '-'}</td>
                  <td className={td}>{p.liveness}{p.lastSeenMin != null ? ` · ${formatAgo(p.lastSeenMin)}` : ''}</td>
                  {isAdmin && <td className={td}>{p.client ? <Chip chip={p.client} onClick={onChipClick} /> : 'pool'}</td>}
                  {isAdmin && <td className={td}>{p.host ? <Chip chip={p.host} onClick={onChipClick} /> : '-'}</td>}
                  <td className={cn(td, 'tabular-nums')}>{p.profiles}</td>
                  <td className={cn(td, 'tabular-nums')}>{p.used}</td>
                  <td className={cn(td, 'tabular-nums')}>{p.free}</td>
                  <td className={cn(td, 'tabular-nums font-medium')}>{p.usableNow}</td>
                  <td className={cn(td, 'text-muted-foreground')}>{(p.freeUserIds || []).join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(c?.notes || []).map((n) => <p key={n} className="text-[11px] text-muted-foreground">{n}</p>)}
    </div>
  );
}

function CapacityGroupTable({ title, rows, first, extra, extraLabel }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <div className="border-b border-border px-3 py-2 text-xs font-medium">{title}</div>
      <table className="w-full text-xs">
        <thead className="bg-secondary/40">
          <tr>
            <th className={th}>{extraLabel || 'Location'}</th>{extra && <th className={th}>State</th>}
            <th className={th}>Phones</th><th className={th}>Profiles</th><th className={th}>Used</th><th className={th}>Free</th>
            <th className={th}>Ready now</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((g, i) => (
            <tr key={`${g.label}-${i}`} className="border-t border-border">
              <td className={td}>{first(g)}</td>{extra && <td className={td}>{extra(g)}</td>}
              <td className={cn(td, 'tabular-nums')}>{g.phones}</td><td className={cn(td, 'tabular-nums')}>{g.profiles}</td>
              <td className={cn(td, 'tabular-nums')}>{g.used}</td><td className={cn(td, 'tabular-nums')}>{g.free}</td>
              <td className={cn(td, 'tabular-nums font-medium')}>{g.usableNow}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ================================================================================================= Assign / move

const EMPTY_FORM = { mode: 'auto', profileId: null, phoneId: null, slotMode: 'keep', slotId: null, slotLabel: '',
  claimPhone: true, allowSharedPhone: false, note: '' };

export function AssignDialog({ open, onOpenChange, account, onDone }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [options, setOptions] = useState([]);
  const [plan, setPlan] = useState(null);
  const [confirmLive, setConfirmLive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [pickSlot, setPickSlot] = useState(false);

  useEffect(() => {
    if (!open || !account) return undefined;
    setForm(EMPTY_FORM);
    setPlan(null);
    setConfirmLive(false);
    setError(null);
    let alive = true;
    // Re-review should-fix 1: Capacity counts for THIS account (its own client, or none) - the profiles Assign accepts.
    Promise.resolve(inventoryApi.capacity(capacityParams({ platform: account.platform, accounts: 50, clientId: account.clientId,
      accountId: account.id })))
      .then((r) => { if (alive) setOptions(planOptions(r?.data)); })
      .catch(() => { if (alive) setOptions([]); });
    return () => { alive = false; };
  }, [open, account]);

  const set = (patch) => { setForm((f) => ({ ...f, ...patch })); setPlan(null); setConfirmLive(false); setError(null); };
  const problem = assignProblem(form);

  const send = async (dryRun) => {
    setBusy(true);
    setError(null);
    try {
      const r = await inventoryApi.assign(assignBody(account.id, form, { dryRun, confirm: confirmLive }));
      if (dryRun) {
        setPlan(r.data);
      } else {
        toast.success(r.data.opId ? `Done: assign #${r.data.opId}${r.data.summary ? ` - ${r.data.summary}` : ''}` : 'Nothing to change');
        onDone?.(r.data);
        onOpenChange(false);
      }
    } catch (e) {
      const live = needsConfirm(e);
      if (live) {
        setPlan(live);
      } else {
        setError(errorText(e, 'Refused - nothing was changed'));
      }
    } finally {
      setBusy(false);
    }
  };

  const liveSlots = plan?.liveSlots || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="inv-assign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ArrowLeftRight className="h-4 w-4" /> Assign / move {account?.label}</DialogTitle>
          <DialogDescription>
            Puts the account on a free Android profile (logged in History, undo in History &gt; Assignments). The worker&apos;s own slot
            settings are not changed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-xs">
          {[['auto', 'The best free profile (Capacity plan)'], ['pick', 'Pick a free profile'], ['release', 'Release: take it off its profile'],
            ['slotOnly', 'Only change the slot link']].map(([k, label]) => (
            <label key={k} className="flex items-center gap-2">
              <input type="radio" name="inv-assign-mode" checked={form.mode === k} onChange={() => set({ mode: k })} aria-label={label} />
              {label}
            </label>
          ))}
          {form.mode === 'pick' && (
            <select aria-label="Free profile" value={form.profileId || ''} onChange={(e) => set({ profileId: e.target.value ? Number(e.target.value) : null })}
              className="h-8 w-full rounded-md border border-border bg-background px-2">
              <option value="">{options.length ? 'Choose…' : 'No free profile in reach'}</option>
              {options.map((o) => <option key={o.id} value={o.id}>{o.label} ({o.tierLabel})</option>)}
            </select>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-muted-foreground">Slot link:</span>
            <select aria-label="Slot link" value={form.slotMode} onChange={(e) => set({ slotMode: e.target.value, slotId: null, slotLabel: '' })}
              className="h-8 rounded-md border border-border bg-background px-2">
              <option value="keep">keep as it is</option>
              <option value="set">link to a slot…</option>
              <option value="unlink">unlink</option>
            </select>
            {form.slotMode === 'set' && (pickSlot
              ? <LinkPicker refTable="slot" allowClear={false} onPick={(ch) => { if (ch) set({ slotId: ch.id, slotLabel: ch.label }); setPickSlot(false); }} onCancel={() => setPickSlot(false)} />
              : <button type="button" className={btn} onClick={() => setPickSlot(true)}>{form.slotLabel || 'Pick slot'}</button>)}
          </div>
          {form.mode !== 'release' && form.mode !== 'slotOnly' && (
            <>
              {/* Re-review must-fix 1: a shared phone is never claimed - "allow shared" unticks and disables the claim. */}
              <label className={cn('flex items-center gap-2', form.allowSharedPhone && 'text-muted-foreground')}
                title={form.allowSharedPhone ? 'A shared phone is never given to one client: untick "Allow a phone shared" to claim a pool phone' : ''}>
                <input type="checkbox" aria-label="Claim phone" checked={form.claimPhone && !form.allowSharedPhone} disabled={form.allowSharedPhone}
                  onChange={(e) => set({ claimPhone: e.target.checked })} />
                Give an unowned (pool) phone to this account&apos;s client</label>
              <label className="flex items-center gap-2"><input type="checkbox" aria-label="Allow shared phone" checked={form.allowSharedPhone}
                onChange={(e) => set(e.target.checked ? { allowSharedPhone: true, claimPhone: false } : { allowSharedPhone: false })} />
                Allow a phone shared with another client (the phone stays unowned)</label>
            </>
          )}
          <input aria-label="Note" value={form.note} onChange={(e) => set({ note: e.target.value })} placeholder="Note (optional, goes into History)"
            className="h-8 w-full rounded-md border border-border bg-background px-2" />
        </div>

        {plan && (
          <div className="space-y-1.5 rounded-md border border-border p-2 text-xs" data-testid="inv-assign-plan">
            {(plan.changes || []).map((c) => <div key={c}>{c}</div>)}
            {(plan.warnings || []).map((w) => <div key={w} className="text-warning">{w}</div>)}
            {plan.workerHint && <div className="text-muted-foreground">{plan.workerHint}</div>}
            {liveSlots.length > 0 && (
              <div className="rounded-md border border-danger/30 bg-danger/10 p-2 text-danger" data-testid="inv-assign-live">
                <div className="flex items-center gap-1 font-medium"><ShieldAlert className="h-3.5 w-3.5" /> This changes a LIVE slot</div>
                {liveSlotLines(liveSlots).map((l) => <div key={l}>{l}</div>)}
                <label className="mt-1 flex items-center gap-2 text-foreground">
                  <input type="checkbox" checked={confirmLive} onChange={(e) => setConfirmLive(e.target.checked)} aria-label="Confirm live slot" />
                  I confirm: change the live slot&apos;s inventory record
                </label>
              </div>
            )}
          </div>
        )}
        {(error || problem) && <p className="text-xs text-danger" role="alert">{error || problem}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className={btn} disabled={busy || !!problem} onClick={() => send(true)}>Preview</button>
          <button type="button" className={btnPrimary} disabled={busy || !!problem || (liveSlots.length > 0 && !confirmLive)}
            onClick={() => send(false)}>Apply</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ================================================================================================= Assignments log

export function AssignmentsLog({ isAdmin, onChipClick }) {
  const logQ = useQuery({
    queryKey: ['inventory-assignments'],
    queryFn: () => inventoryApi.assignments({ size: 200 }).then((r) => r.data),
    retry: false,
  });
  // Phase 3 review should-fix 8: one undo at a time. The ref stops a second click in the same tick (before the
  // re-render disables the buttons); the state disables every Undo button and labels the running one.
  const [undoing, setUndoing] = useState(null);
  const undoInFlight = useRef(false);
  const undo = async (op) => {
    if (undoInFlight.current) return;
    undoInFlight.current = true;
    try {
      if (!window.confirm(`Undo assign #${op.id}? ${op.summary || ''}`)) return;
      setUndoing(op.id);
      await runUndo(op);
    } finally {
      undoInFlight.current = false;
      setUndoing(null);
    }
  };
  const runUndo = async (op) => {
    try {
      let r;
      try {
        r = await inventoryApi.undoAssign(op.id, {});
      } catch (e) {
        const live = needsConfirm(e);
        if (!live) throw e;
        if (!window.confirm(`This changes a LIVE slot:\n${liveSlotLines(live.liveSlots).join('\n')}\n\nUndo anyway?`)) return;
        r = await inventoryApi.undoAssign(op.id, { confirmLiveSlot: true });
      }
      toast.success(`Undone: ${r.data.summary}`);
      logQ.refetch?.();
    } catch (e) {
      toast.error(errorText(e, 'Undo refused - nothing was changed'));
    }
  };
  if (logQ.error) return <p className="text-sm text-danger" role="alert">{errorText(logQ.error)}</p>;
  const rows = logQ.data?.rows || [];
  return (
    <div className="overflow-x-auto rounded-lg border border-border" data-testid="inv-assignments">
      <table className="w-full text-xs">
        <thead className="bg-secondary/40">
          <tr>
            <th className={th}>#</th><th className={th}>When</th><th className={th}>Kind</th><th className={th}>Account</th>
            <th className={th}>What changed</th><th className={th}>State</th>{isAdmin && <th className={th}>By</th>}{isAdmin && <th className={th} />}
          </tr>
        </thead>
        <tbody>
          {rows.map((op) => (
            <tr key={op.id} className="border-t border-border" data-testid="inv-assignment-row">
              <td className={cn(td, 'tabular-nums')}>{op.id}</td>
              <td className={cn(td, 'whitespace-nowrap')}>{(op.at || '').replace('T', ' ').slice(0, 16)}</td>
              <td className={td}>{op.kind === 'undo' ? `undo of #${op.undoOf}` : op.kind}{op.liveSlotConfirmed ? ' (live slot confirmed)' : ''}</td>
              <td className={td}>{op.account ? <Chip chip={op.account} onClick={onChipClick} /> : '-'}</td>
              <td className={td}>{op.summary}{op.note ? <div className="text-muted-foreground">{op.note}</div> : null}</td>
              <td className={td}>{op.state}</td>
              {isAdmin && <td className={cn(td, 'text-muted-foreground')}>{op.actor}</td>}
              {isAdmin && (
                <td className={td}>
                  {op.canUndo && (
                    <button type="button" className={btn} onClick={() => undo(op)} aria-label={`Undo assign ${op.id}`}
                      disabled={undoing != null}>
                      <Undo2 className="h-3.5 w-3.5" /> {undoing === op.id ? 'Undoing…' : 'Undo'}
                    </button>
                  )}
                </td>
              )}
            </tr>
          ))}
          {logQ.data && rows.length === 0 && (
            <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">No assign / move yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
